import { enqueueSnackbar, DownloadFile } from 'actions';
import {
	ProcessMocnWorkRequest,
	ProcessEnodebPairingWorkRequest,
	GenerateRegenerationAuditRequest,
	RunEricssonAuditRequest,
	ProcessAlarmsDiffRequest,
	RunNokiaDescriptionChecksRequest,
	RunNokiaDescriptionDiffRequest
} from '../requests/ScriptsGenerationRequests';

// Action Types
export const SET_FORM_STATE = 'SET_FORM_STATE';
export const RESET_FIELDS = 'RESET_FIELDS';
export const GET_CLUSTERS_INFO = 'GET_CLUSTERS_INFO';
export const SET_LOADING = 'SET_LOADING';
export const SET_PROCESSING_RESULT = 'SET_PROCESSING_RESULT';
export const SET_ENODEB_PAIRING_LOADING = 'SET_ENODEB_PAIRING_LOADING';
export const SET_ENODEB_PAIRING_RESULT = 'SET_ENODEB_PAIRING_RESULT';

// Nokia Audit Action Types
export const GENERATE_REGENERATION_AUDIT_REQUEST = 'GENERATE_REGENERATION_AUDIT_REQUEST';
export const GENERATE_REGENERATION_AUDIT_SUCCESS = 'GENERATE_REGENERATION_AUDIT_SUCCESS';
export const GENERATE_REGENERATION_AUDIT_FAILURE = 'GENERATE_REGENERATION_AUDIT_FAILURE';

// Ericsson Audit Action Types
export const RUN_ERICSSON_AUDIT_REQUEST = 'RUN_ERICSSON_AUDIT_REQUEST';
export const RUN_ERICSSON_AUDIT_SUCCESS = 'RUN_ERICSSON_AUDIT_SUCCESS';
export const RUN_ERICSSON_AUDIT_FAILURE = 'RUN_ERICSSON_AUDIT_FAILURE';

// Alarms Diff Action Types
export const PROCESS_ALARMS_DIFF_REQUEST = 'PROCESS_ALARMS_DIFF_REQUEST';
export const PROCESS_ALARMS_DIFF_SUCCESS = 'PROCESS_ALARMS_DIFF_SUCCESS';
export const PROCESS_ALARMS_DIFF_FAILURE = 'PROCESS_ALARMS_DIFF_FAILURE';

// Nokia Description Checks Action Types
export const RUN_NOKIA_DESC_CHECKS_REQUEST = 'RUN_NOKIA_DESC_CHECKS_REQUEST';
export const RUN_NOKIA_DESC_CHECKS_SUCCESS = 'RUN_NOKIA_DESC_CHECKS_SUCCESS';
export const RUN_NOKIA_DESC_CHECKS_FAILURE = 'RUN_NOKIA_DESC_CHECKS_FAILURE';

// Nokia Description Diff Action Types
export const RUN_NOKIA_DESCRIPTION_DIFF_REQUEST = 'RUN_NOKIA_DESCRIPTION_DIFF_REQUEST';
export const RUN_NOKIA_DESCRIPTION_DIFF_SUCCESS = 'RUN_NOKIA_DESCRIPTION_DIFF_SUCCESS';
export const RUN_NOKIA_DESCRIPTION_DIFF_FAILURE = 'RUN_NOKIA_DESCRIPTION_DIFF_FAILURE';

// Action Creators
export const SetFormState = (formState) => ({
	type: SET_FORM_STATE,
	formState
});

export const ResetFields = () => ({
	type: RESET_FIELDS
});

export const GetClustersInfo = (clustersInfo) => ({
	type: GET_CLUSTERS_INFO,
	clustersInfo
});

export const SetLoading = (isLoading) => ({
	type: SET_LOADING,
	isLoading
});

export const SetProcessingResult = (result) => ({
	type: SET_PROCESSING_RESULT,
	result
});

export const SetEnodebPairingLoading = (isLoading) => ({
	type: SET_ENODEB_PAIRING_LOADING,
	isLoading
});

export const SetEnodebPairingResult = (result) => ({
	type: SET_ENODEB_PAIRING_RESULT,
	result
});

// Async Action for Processing MOCN Work
export const ProcessMocnWork = (request, excelFile = null) => {
	return async (dispatch) => {
		try {
			dispatch(SetLoading(true));
			dispatch(SetProcessingResult(null));

			const formData = new FormData();
			formData.append('DeploymentDate', request.deploymentDate);
			formData.append('UseDatabase', request.useDatabase.toString());
			
			if (request.scfFolderPath) {
				formData.append('ScfFolderPath', request.scfFolderPath);
			}
			
			// Add Excel file if provided
			if (excelFile) {
				formData.append('excelFile', excelFile);
			}

			// Call ProcessMocnWork endpoint
			const response = await ProcessMocnWorkRequest(formData);

			if (response.status === 200) {
				dispatch(SetProcessingResult(response.data));
				
				// Check if response contains file data for download
				if (response.data.zipFilePath) {
					dispatch(DownloadFile(response.data.zipFilePath));
				} else {
					dispatch(enqueueSnackbar({
						message: `Successfully processed ${response.data.processedSites || 0} Files`,
						options: { variant: 'success' }
					}));
				}
			} else {
				const errorMessage = response.data?.errorMessage || response.data?.error || 'Processing failed';
				dispatch(SetProcessingResult({
					success: false,
					errorMessage: errorMessage
				}));
				dispatch(enqueueSnackbar({
					message: errorMessage,
					options: { variant: 'error' }
				}));
			}
		} catch (error) {
			// Try to extract the most specific error message
			let errorMessage = 'Network error!';
			let fullResult = null;
			
			if (error.response?.data) {
				// If backend returned the full MocnBatchResult with error
				if (error.response.data.success === false && error.response.data.error) {
					errorMessage = error.response.data.error;
					fullResult = error.response.data; // Keep the full result for display
				} else if (typeof error.response.data === 'string') {
					errorMessage = error.response.data;
				} else if (error.response.data.error) {
					errorMessage = error.response.data.error;
				} else if (error.response.data.errorMessage) {
					errorMessage = error.response.data.errorMessage;
				} else if (error.response.data.message) {
					errorMessage = error.response.data.message;
				} else if (error.response.data.title) {
					errorMessage = error.response.data.title;
					if (error.response.data.errors) {
						const validationErrors = Object.entries(error.response.data.errors)
							.map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
							.join('; ');
						errorMessage += ` - ${validationErrors}`;
					}
				} else {
					errorMessage = JSON.stringify(error.response.data);
				}
			} else if (error.message) {
				errorMessage = error.message;
			}
			
			// If we have a full result object, use it; otherwise create a minimal one
			const resultToDispatch = fullResult || {
				success: false,
				errorMessage: errorMessage
			};
			
			dispatch(SetProcessingResult({
				success: false,
				errorMessage: errorMessage
			}));
			
			dispatch(enqueueSnackbar({
				message: errorMessage,
				options: { variant: 'error' }
			}));
		} finally {
			dispatch(SetLoading(false));
		}
	};
};

// Async Action for Generating Nokia Audit
export const GenerateRegenerationAudit = (excelFile, deploymentDate) => {
	return async (dispatch) => {
		try {
			dispatch({ type: GENERATE_REGENERATION_AUDIT_REQUEST });

			// Create FormData for file upload
			const formData = new FormData();
			formData.append('excelFile', excelFile);
			formData.append('deploymentDate', deploymentDate);
			formData.append('useDatabase', 'true'); // Always use database for audit

			const response = await GenerateRegenerationAuditRequest(formData);

			if (response.status === 200 && response.data.success) {
				dispatch({
					type: GENERATE_REGENERATION_AUDIT_SUCCESS,
					payload: {
						success: true,
						totalSites: response.data.totalSites || 0,
						processedSites: response.data.processedSites || 0,
						failedSites: response.data.failedSites || 0,
						zipFilePath: response.data.zipFilePath,
						excelReportPath: response.data.excelReportPath,
						summary: response.data.summary
					}
				});

				// Auto-download Excel report
				if (response.data.excelReportPath) {
					dispatch(DownloadFile(response.data.excelReportPath));
				}

				// Auto-download ZIP if available
				if (response.data.zipFilePath) {
					dispatch(DownloadFile(response.data.zipFilePath));
				}

				dispatch(enqueueSnackbar({
					message: `Regeneration audit completed! Processed ${response.data.processedSites || 0} sites.`,
					options: { variant: 'success' }
				}));
			} else {
				const errorMessage = response.data?.errorMessage || 'Regeneration audit failed';
				dispatch({
					type: GENERATE_REGENERATION_AUDIT_FAILURE,
					payload: errorMessage
				});
				dispatch(enqueueSnackbar({
					message: errorMessage,
					options: { variant: 'error' }
				}));
			}
		} catch (error) {
			console.error('Regeneration audit error:', error);
			
			let errorMessage = 'Network error during regeneration audit!';
			
			if (error && error.response && error.response.data) {
				errorMessage = error.response.data.errorMessage || 
				              error.response.data.error ||
				              error.response.data.message ||
				              errorMessage;
			} else if (error && error.message) {
				errorMessage = error.message;
			}
			
			dispatch({
				type: GENERATE_REGENERATION_AUDIT_FAILURE,
				payload: errorMessage
			});
			
			dispatch(enqueueSnackbar({
				message: errorMessage,
				options: { variant: 'error' }
			}));
		}
	};
};

// Async Action for Processing EnodebPairing Work
export const ProcessEnodebPairingWork = (request, excelFile) => {
	return async (dispatch) => {
		try {
			dispatch(SetEnodebPairingLoading(true));
			dispatch(SetEnodebPairingResult(null));

			// Create FormData for file upload (matching MOCN pattern)
			const formData = new FormData();
			formData.append('deploymentDate', request.deploymentDate);
			formData.append('useDatabase', request.useDatabase.toString());
			
			if (request.scfFolderPath) {
				formData.append('scfFolderPath', request.scfFolderPath);
			}
			
			// Add Excel file if provided
			if (excelFile) {
				formData.append('excelFile', excelFile);
			}

			const response = await ProcessEnodebPairingWorkRequest(formData);

			if (response.status === 200) {
				dispatch(SetEnodebPairingResult({
					success: true,
					totalSites: response.data.totalSites || 0,
					processedSites: response.data.processedSites || 0,
					failedSites: response.data.failedSites || 0,
					siteResults: response.data.siteResults || [],
					zipFilePath: response.data.zipFilePath,
					deploymentDate: response.data.deploymentDate,
					processedAt: response.data.processedAt
				}));

				// Auto-download ZIP if available
				if (response.data.zipFilePath) {
					dispatch(DownloadFile(response.data.zipFilePath));
				}

				dispatch(enqueueSnackbar({
					message: response.data.message || `EnodebPairing completed successfully. Processed ${response.data.processedSites || 0} || 0} files.`,
					options: { variant: 'success' }
				}));
			} else {
				const errorMessage = response.data?.error || 'EnodebPairing processing failed';
				dispatch(SetEnodebPairingResult({
					success: false,
					errorMessage: errorMessage
				}));
				dispatch(enqueueSnackbar({
					message: errorMessage,
					options: { variant: 'error' }
				}));
			}
		} catch (error) {
			console.error('EnodebPairing processing error:', error);
			
			const errorMessage = error?.response?.data?.error || 
			                    error?.response?.data?.message ||
			                    error?.message || 
			                    'Network error during EnodebPairing processing!';
			
			dispatch(SetEnodebPairingResult({
				success: false,
				errorMessage: errorMessage
			}));
			
			dispatch(enqueueSnackbar({
				message: errorMessage,
				options: { variant: 'error' }
			}));
		} finally {
			dispatch(SetEnodebPairingLoading(false));
		}
	};
};

// Async Action for Running Ericsson Audit
export const RunEricssonAudit = (excelFile) => {
	return async (dispatch) => {
		try {
			dispatch({ type: RUN_ERICSSON_AUDIT_REQUEST });

			const formData = new FormData();
			formData.append('excelFile', excelFile);

			const response = await RunEricssonAuditRequest(formData);

			if (response.status === 200 && response.data.success) {
				dispatch({
					type: RUN_ERICSSON_AUDIT_SUCCESS,
					payload: {
						sitesProcessed: response.data.summary?.sitesProcessed || 0,
						totalDifferences: response.data.summary?.totalDifferences || 0,
						excelReportPath: response.data.excelReportPath
					}
				});

				// Auto-download the Excel report
				if (response.data.excelReportPath) {
					dispatch(DownloadFile(response.data.excelReportPath));
				}

				dispatch(enqueueSnackbar({
					message: `Ericsson Audit completed! ${response.data.summary?.sitesProcessed || 0} site(s) processed, ${response.data.summary?.totalDifferences || 0} difference(s) found.`,
					options: { variant: 'success' }
				}));
			} else {
				const errorMessage = response.data?.error || response.data?.errorMessage || 'Ericsson Audit failed';
				dispatch({ type: RUN_ERICSSON_AUDIT_FAILURE, payload: errorMessage });
				dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } }));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.error ||
			                    error?.response?.data?.message ||
			                    error?.message ||
			                    'Network error during Ericsson Audit!';

			dispatch({ type: RUN_ERICSSON_AUDIT_FAILURE, payload: errorMessage });
			dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } }));
		}
	};
};

// Async Action for Processing Alarms Diff
export const ProcessAlarmsDiff = (zipFile) => {
	return async (dispatch) => {
		try {
			dispatch({ type: PROCESS_ALARMS_DIFF_REQUEST });

			const formData = new FormData();
			formData.append('zipFile', zipFile);

			const response = await ProcessAlarmsDiffRequest(formData);

			if (response.status === 200 && response.data.success) {
				dispatch({
					type: PROCESS_ALARMS_DIFF_SUCCESS,
					payload: {
						success: true,
						preAlarmCount: response.data.preAlarmCount,
						postAlarmCount: response.data.postAlarmCount,
						newAlarmCount: response.data.newAlarmCount,
						excelFilePath: response.data.excelFilePath,
						message: response.data.message
					}
				});

				// Auto-download the Excel report
				if (response.data.excelFilePath) {
					dispatch(DownloadFile(response.data.excelFilePath));
				}

				dispatch(enqueueSnackbar({
					message: response.data.message || 'Alarms diff completed successfully.',
					options: { variant: 'success' }
				}));
			} else {
				const errorMessage = response.data?.error || 'Alarms diff processing failed';
				dispatch({ type: PROCESS_ALARMS_DIFF_FAILURE, payload: errorMessage });
				dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } }));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.error ||
			                    error?.response?.data?.message ||
			                    error?.message ||
			                    'Network error during alarms diff processing!';

			dispatch({ type: PROCESS_ALARMS_DIFF_FAILURE, payload: errorMessage });
			dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } }));
		}
	};
};

// Async Action for Running Nokia Description Checks
export const RunNokiaDescriptionChecks = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: RUN_NOKIA_DESC_CHECKS_REQUEST });

			const response = await RunNokiaDescriptionChecksRequest();

			if (response.status === 200 && response.data.success) {
				dispatch({
					type: RUN_NOKIA_DESC_CHECKS_SUCCESS,
					payload: response.data
				});

				if (response.data.excelReportPath) {
					dispatch(DownloadFile(response.data.excelReportPath));
				}

				dispatch(enqueueSnackbar({
					message: `Description checks complete. ${response.data.summary?.sitesChecked || 0} sites checked, ${response.data.summary?.findingsCount || 0} finding(s).`,
					options: { variant: 'success' }
				}));
			} else {
				const errorMessage = response.data?.error || 'Description checks failed';
				dispatch({ type: RUN_NOKIA_DESC_CHECKS_FAILURE, payload: errorMessage });
				dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } }));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.error ||
                                 error?.response?.data?.message ||
                                 error?.message ||
                                 'Network error during description checks!';
			dispatch({ type: RUN_NOKIA_DESC_CHECKS_FAILURE, payload: errorMessage });
			dispatch(enqueueSnackbar({ message: errorMessage, options: { variant: 'error' } }));
		}
	};
};

// Async Action for Nokia Description Diff
// Uploads the Excel, calls the diff endpoint, and auto download the report
export const RunNokiaDescriptionDiff = (excelFile) => {
	return async (dispatch) => {
		try {
			dispatch({ type: RUN_NOKIA_DESCRIPTION_DIFF_REQUEST });
 
			const formData = new FormData();
			formData.append('excelFile', excelFile);
 
			const response = await RunNokiaDescriptionDiffRequest(formData);
 
			if (response.status === 200 && response.data.success) {
				dispatch({
					type: RUN_NOKIA_DESCRIPTION_DIFF_SUCCESS,
					payload: {
						success:              true,
						totalRows:            response.data.totalRows,
						matchCount:           response.data.matchCount,
						mismatchCount:        response.data.mismatchCount,
						notFoundCount:        response.data.notFoundCount,
						unexpectedVf3ReqCount: response.data.unexpectedVf3ReqCount,
						excelReportPath:      response.data.excelReportPath
					}
				});
 
				// Auto-download the Excel report
				if (response.data.excelReportPath) {
					dispatch(DownloadFile(response.data.excelReportPath));
				}
 
				dispatch(enqueueSnackbar({
					// eslint-disable-next-line max-len
					message: `Description Diff complete! ${response.data.matchCount} match, ${response.data.mismatchCount} mismatch, ${response.data.notFoundCount} not found, ${response.data.unexpectedVf3ReqCount} unexpected VF3REQ.`,
					options: { variant: 'success' }
				}));
			} else {
				const errorMessage = response.data?.error || 'Nokia Description Diff failed';
				dispatch({
					type: RUN_NOKIA_DESCRIPTION_DIFF_FAILURE,
					payload: errorMessage
				});
				dispatch(enqueueSnackbar({
					message: errorMessage,
					options: { variant: 'error' }
				}));
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.error ||
			                     error?.message ||
			                     'Network error during Nokia Description Diff!';
 
			dispatch({
				type: RUN_NOKIA_DESCRIPTION_DIFF_FAILURE,
				payload: errorMessage
			});
			dispatch(enqueueSnackbar({
				message: errorMessage,
				options: { variant: 'error' }
			}));
		}
	};
};
